import { DATE_FORMATS, formatDate } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { User } from '@/types/directory/User';

import i18n from '@/plugins/i18n';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

const SHORT_DATE_WITH_TIME = `${DATE_FORMATS.DISPLAY_DATE_SHORT} ${DATE_FORMATS.DISPLAY_TIME}`;

interface DiscussionEntryHeaderParams {
    /** Resolved by the caller; `undefined` when the entry has no author or they no longer resolve. */
    author?: User;
    /** The ISO moment the entry is dated at. */
    date: string;
    /** For entries that can land several times in one day, such as a key result's moves. */
    withTimeOfDay?: boolean;
}

/**
 * The author and date every entry of an objective's "Discussion" thread hands to the library's
 * `DiscussionComment` and `DiscussionActivity`: an author who does not resolve reads as unknown,
 * and the date is the short one of the thread.
 */
export const useDiscussionEntryHeader = ({
    author,
    date,
    withTimeOfDay = false,
}: DiscussionEntryHeaderParams) => {
    const { t } = useTranslation();

    return {
        authorName: author ? getFullName(author) : t('objectives.detail.discussion.unknownAuthor'),
        avatarSrc: author ? getProfilePictureUrl(author.profilePictureUrl) : undefined,
        avatarInitials: author ? getInitials(author) : undefined,
        dateTime: date,
        dateLabel: formatDate({
            date,
            format: withTimeOfDay ? SHORT_DATE_WITH_TIME : DATE_FORMATS.DISPLAY_DATE_SHORT,
            language: i18n.language,
        }),
    };
};
